import * as Sentry from "@sentry/react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import Dashboard from "../pages/Dashboard";
import Documentation from "../pages/Documentation";
import { ApiProvider } from "../contexts/ApiContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import GlobalBanner from "@/components/shared-ui/GlobalBanner";
import UsefulDocuments from "@/pages/UsefulDocuments";
import CustomErrorBoundary from "@/components/utility/CustomErrorBoundary";
import Contact from "@/pages/Contact";
import { ErrorBoundary } from "react-error-boundary";
import ErrorScreen from "@/components/shared-ui/ErrorScreen";
import NotFound from "@/pages/NotFound";
import UiTraining from "@/pages/UiTraining";
import ApiTraining from "@/pages/ApiTraining";
import { DocsActiveHeadingProvider } from "@/contexts/DocsActiveHeadingContext";
import DueDiligence from "@/pages/DueDiligence";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUserManagement from "@/pages/admin/AdminUserManagement";
import AdminUserEdit from "@/pages/admin/AdminUserEdit";
import AdminUserCreate from "@/pages/admin/AdminUserCreate";
import UpdatesList from "@/pages/UpdatesList";
import Update from "@/pages/Update";
import RoleGuard from "@/components/utility/RoleGuard";
import Configuration from "@/pages/Configuration";
import ServiceTargets from "@/pages/ServiceTargets";
import AccountManagement from "@/pages/AccountManagement";
import ProjectSummary from "@/pages/ProjectSummary";
import ProductRoadmap from "@/pages/ProductRoadmap";
import AdminUsersDownload from "@/pages/admin/AdminUsersDownload";
import TransferProviderList from "@/pages/TransferProviderList";
import TransferProvider from "@/pages/TransferProvider";

const Routing = () => {
  const logSentryError = (error: Error) => {
    Sentry.captureException(error);
  };

  return (
    // API Provider must be within authenticated environment
    <ApiProvider>
      <ErrorBoundary
        fallback={<ErrorScreen topLevel />}
        onError={logSentryError}
      >
        <GlobalBanner />
        <CompanyProvider>
          {/* Commenting out the Product Fruits survey until we're ready to use it again */}
          {/* <ProductFruitsSurvey /> */}
          <Routes>
            <Route
              element={
                <CustomErrorBoundary handleError={logSentryError}>
                  <Outlet />
                </CustomErrorBoundary>
              }
            >
              {/* Customer section */}
              <Route path="/" element={<Dashboard />} />
              <Route
                path="/docs"
                element={
                  <Navigate replace to="/docs/welcome/getting-started" />
                }
              />
              <Route
                path={`/docs/:docSlug/:sectionSlug`}
                element={
                  <DocsActiveHeadingProvider>
                    <Documentation />
                  </DocsActiveHeadingProvider>
                }
              />
              <Route
                path="/due-diligence"
                element={<Navigate replace to="/due-diligence/overview" />}
              />
              <Route
                path={`/due-diligence/:ddSlug`}
                element={<DueDiligence />}
              />
              <Route
                path="/ui-training"
                element={
                  <Navigate
                    replace
                    to="/ui-training/seccl-system-training-introduction"
                  />
                }
              />
              <Route path="/ui-training/:videoSlug" element={<UiTraining />} />
              <Route
                path="/api-training"
                element={
                  <Navigate
                    replace
                    to="/api-training/creating-a-client-via-an-illustration"
                  />
                }
              />
              <Route
                path="/api-training/:videoSlug"
                element={<ApiTraining />}
              />
              <Route path="/useful-documents" element={<UsefulDocuments />} />
              <Route
                path="/project-summary"
                element={
                  <RoleGuard
                    roleRequired="all_features"
                    statusDenied="other"
                    component={() => (
                      <DocsActiveHeadingProvider>
                        <ProjectSummary />
                      </DocsActiveHeadingProvider>
                    )}
                  />
                }
              />
              <Route
                path={`/project-summary/:docSlug/:sectionSlug`}
                element={
                  <RoleGuard
                    roleRequired="all_features"
                    statusDenied="other"
                    component={() => (
                      <DocsActiveHeadingProvider>
                        <ProjectSummary />
                      </DocsActiveHeadingProvider>
                    )}
                  />
                }
              />
              <Route
                path="/updates"
                element={
                  <RoleGuard
                    roleRequired="basic"
                    statusDenied="other"
                    component={() => <UpdatesList />}
                  />
                }
              />
              <Route
                path="/updates/:updateSlug"
                element={
                  <RoleGuard
                    roleRequired="basic"
                    statusDenied="other"
                    component={() => (
                      <DocsActiveHeadingProvider>
                        <Update />
                      </DocsActiveHeadingProvider>
                    )}
                  />
                }
              />
              <Route
                path="/transfer-providers"
                element={
                  <RoleGuard
                    roleRequired="basic"
                    statusDenied="other"
                    component={() => <TransferProviderList />}
                  />
                }
              />
              <Route
                path="/transfer-providers/:transferProviderSlug"
                element={
                  <RoleGuard
                    roleRequired="basic"
                    statusDenied="other"
                    component={() => (
                      <DocsActiveHeadingProvider>
                        <TransferProvider />
                      </DocsActiveHeadingProvider>
                    )}
                  />
                }
              />
              <Route
                path="/configuration"
                element={
                  <RoleGuard
                    roleRequired="all_features"
                    statusDenied={["other", "integration"]}
                    component={() => <Configuration />}
                  />
                }
              />
              <Route
                path="/account-management"
                element={
                  <RoleGuard
                    roleRequired="all_features"
                    statusDenied={["other", "integration"]}
                    component={() => <AccountManagement />}
                  />
                }
              />
              <Route
                path="/service-targets"
                element={
                  <RoleGuard
                    roleRequired="all_features"
                    statusDenied={["other", "integration"]}
                    component={() => <ServiceTargets />}
                  />
                }
              />
              <Route
                path="/product-roadmap"
                element={
                  <RoleGuard
                    roleRequired="basic"
                    statusDenied="other"
                    component={() => <ProductRoadmap />}
                  />
                }
              />
              <Route path="/contact" element={<Contact />} />
              {/* Admin section */}
              <Route
                path="/admin"
                element={
                  <RoleGuard
                    roleRequired="admin"
                    component={() => <AdminDashboard />}
                  />
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RoleGuard
                    roleRequired="admin"
                    component={() => <AdminUserManagement />}
                  />
                }
              />
              <Route
                path="/admin/users/download"
                element={
                  <RoleGuard
                    roleRequired="admin"
                    component={() => <AdminUsersDownload />}
                  />
                }
              />
              <Route
                path="/admin/users/create"
                element={
                  <RoleGuard
                    roleRequired="admin"
                    component={() => <AdminUserCreate />}
                  />
                }
              />
              <Route
                path="/admin/users/edit/:userId"
                element={
                  <RoleGuard
                    roleRequired="admin"
                    component={() => <AdminUserEdit />}
                  />
                }
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </CompanyProvider>
      </ErrorBoundary>
    </ApiProvider>
  );
};

export default Routing;
